import {
  CAlert,
  CBadge,
  CButton,
  CCol,
  CForm,
  CFormFeedback,
  CLoadingButton,
  CMultiSelect,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilArrowRight, cilImage, cilPeople } from '@coreui/icons'
import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import CommonService from 'src/service/CommonService'
import TeamService from 'src/service/TeamService'
import { resolveTeamSponsorUrl } from 'src/utils/imageUrl'
import SectionBox from './BulkImage/SectionBox'

const TeamSponsorLogo = (props) => {
  const [teamList, setTeamList] = useState([])
  const [multiOption, setMultiOption] = useState([])
  const [allPlayers, setAllPlayers] = useState([])
  const [loader, setLoader] = useState(false)
  const [selectKey, setSelectKey] = useState(0)

  useEffect(() => {
    TeamService.getTeamListByGrade()
      .then((res) => {
        if (res.status === 200) {
          setTeamList(res.data)
          setMultiOption(res.teamArray)
        }
      })
      .catch((e) => {
        ToastComponent(e.response?.data?.message, 'error')
      })
    CommonService.clubPlayers().then((result) => {
      setAllPlayers(result.data)
    })
  }, [])

  const validationSchema = Yup.object().shape({
    team: Yup.array()
      .of(Yup.string())
      .min(1, 'Please select at least one team')
      .required('Please select team'),
  })

  const formik = useFormik({
    initialValues: {
      team: [],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      var formData = new FormData()
      data.team.forEach((teamId) => {
        formData.append('team[]', teamId)
      })
      setLoader(true)
      PlayerService.updateTeamSponsorLogoOnPlayers(formData)
        .then((res) => {
          if (res.status === 200) {
            resetAll()
            props.setRefreshKey((prev) => prev + 1)
            ToastComponent(res.message, 'success')
          } else {
            ToastComponent(res.message, 'error')
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong', 'error')
          setLoader(false)
        })
    },
  })

  const resetAll = () => {
    formik.resetForm({ values: { team: [] } })
    setSelectKey((prev) => prev + 1)
  }

  const playerCountByTeam = allPlayers.reduce((acc, player) => {
    if (player.team_id) {
      acc[player.team_id] = (acc[player.team_id] || 0) + 1
    }
    return acc
  }, {})

  // preserve selection order so the first team picked is the first row shown
  const selectedTeams = formik.values.team
    .map((id) => teamList.find((team) => String(team.id) === String(id)))
    .filter(Boolean)
    .map((team) => ({
      ...team,
      playersCount: playerCountByTeam[team.id] || 0,
    }))

  const readyTeams = selectedTeams.filter((team) => team.image)
  const skippedTeams = selectedTeams.filter((team) => !team.image)
  const totalPlayersAffected = readyTeams.reduce((sum, team) => sum + team.playersCount, 0)

  return (
    <CForm onSubmit={formik.handleSubmit}>
      <CRow className="g-4">
        <CCol md={6}>
          <SectionBox title="1. Select Teams">
            <CMultiSelect
              key={selectKey}
              options={multiOption}
              selectionType="tags"
              name="team"
              placeholder="Search teams..."
              className={formik.errors.team && formik.touched.team ? 'is-invalid' : ''}
              onChange={(selectedOptions) => {
                formik.setFieldValue(
                  'team',
                  selectedOptions.map((option) => option.value),
                )
              }}
              onBlur={() => {
                formik.setFieldTouched('team', true)
              }}
              value={multiOption.filter((obj) => formik.values.team.includes(obj.value))}
            />
            {formik.errors.team && formik.touched.team && (
              <CFormFeedback invalid>{formik.errors.team}</CFormFeedback>
            )}
            <div className="text-medium-emphasis small d-flex align-items-center mt-2">
              <CIcon icon={cilPeople} className="me-1" />
              {selectedTeams.length} teams selected
            </div>
          </SectionBox>
        </CCol>

        <CCol md={6}>
          <SectionBox title="2. Sponsor Logos">
            {selectedTeams.length === 0 && (
              <div className="text-medium-emphasis small">
                Select teams to preview sponsor logos
              </div>
            )}
            {selectedTeams.map((team) => {
              const sponsorUrl = resolveTeamSponsorUrl(team.image)
              return (
                <div
                  key={team.id}
                  className="d-flex align-items-center justify-content-between rounded-3 p-3 mb-2"
                  style={{
                    border: `1px solid ${sponsorUrl ? '#bfe3c9' : '#f2dcae'}`,
                    backgroundColor: sponsorUrl ? '#f4fbf6' : '#fef9f0',
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div className="fw-semibold" style={{ minWidth: 90 }}>
                      {team.name}
                    </div>
                    <CIcon icon={cilArrowRight} className="text-medium-emphasis mx-3" />
                    <div
                      className="d-flex align-items-center justify-content-center rounded-2 border bg-white flex-shrink-0"
                      style={{ width: 48, height: 48 }}
                    >
                      {sponsorUrl ? (
                        <img
                          src={sponsorUrl}
                          alt={team.name}
                          width={40}
                          height={40}
                          style={{ objectFit: 'contain' }}
                        />
                      ) : (
                        <CIcon icon={cilImage} className="text-medium-emphasis" size="lg" />
                      )}
                    </div>
                    <div className="ms-3">
                      <div className="fw-medium small">
                        {sponsorUrl ? 'Sponsor logo' : 'No sponsor logo assigned'}
                      </div>
                      <div className="text-medium-emphasis small">{team.playersCount} players</div>
                    </div>
                  </div>
                  <CBadge
                    color={sponsorUrl ? 'success' : 'warning'}
                    shape="rounded-pill"
                    className="px-3 py-2"
                  >
                    {sponsorUrl ? 'Ready' : 'Will be skipped'}
                  </CBadge>
                </div>
              )
            })}
            {skippedTeams.length > 0 && (
              <CAlert color="warning" className="mt-2 mb-0 py-2">
                {skippedTeams.length} selected team{skippedTeams.length > 1 ? 's' : ''} have no
                sponsor logo and will be skipped.
              </CAlert>
            )}
          </SectionBox>
        </CCol>
      </CRow>

      <SectionBox title="3. Review" className="mt-4">
        <div className="ge-datatable ge-datatable--static">
          <CTable responsive hover className="align-middle mb-0">
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell>Team</CTableHeaderCell>
                <CTableHeaderCell>Sponsor Logo</CTableHeaderCell>
                <CTableHeaderCell>Players Affected</CTableHeaderCell>
                <CTableHeaderCell>Status</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {selectedTeams.length === 0 && (
                <CTableRow>
                  <CTableDataCell colSpan={4} className="text-medium-emphasis text-center">
                    No teams selected yet
                  </CTableDataCell>
                </CTableRow>
              )}
              {selectedTeams.map((team) => {
                const sponsorUrl = resolveTeamSponsorUrl(team.image)
                return (
                  <CTableRow key={team.id}>
                    <CTableDataCell>{team.name}</CTableDataCell>
                    <CTableDataCell>
                      {sponsorUrl ? (
                        <img
                          src={sponsorUrl}
                          alt={team.name}
                          width={36}
                          height={36}
                          style={{ objectFit: 'contain', borderRadius: 4 }}
                        />
                      ) : (
                        <CIcon icon={cilImage} className="text-medium-emphasis" size="xl" />
                      )}
                    </CTableDataCell>
                    <CTableDataCell>{team.playersCount}</CTableDataCell>
                    <CTableDataCell>
                      <CBadge color={sponsorUrl ? 'success' : 'warning'}>
                        {sponsorUrl ? 'Ready' : 'Skipped'}
                      </CBadge>
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
        {selectedTeams.length > 0 && (
          <div className="text-medium-emphasis small mt-2">
            {totalPlayersAffected} player records will be updated across {readyTeams.length} team
            {readyTeams.length !== 1 ? 's' : ''}.
          </div>
        )}
      </SectionBox>

      <div className="d-flex justify-content-end gap-2 mt-4">
        <CButton color="secondary" variant="outline" type="button" onClick={resetAll}>
          Cancel
        </CButton>
        <CLoadingButton type="submit" color="primary" loading={loader}>
          Apply Sponsor Logos to {totalPlayersAffected} Players
        </CLoadingButton>
      </div>
    </CForm>
  )
}

export default TeamSponsorLogo
